import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useToast, ConfirmModal } from "../../../shared/components/SharedUI";
import apiFetch from "../../../shared/services/apiClient";
import { getAvisos, crearAviso, deleteAviso } from "../services/portalService";

const PAGE_SIZE = 8;

function Pagination({ page, total, onChange }) {
  const pages = Math.ceil(total / PAGE_SIZE);
  if (pages <= 1) return null;
  const btnStyle = (disabled) => ({
    padding: "6px 12px", borderRadius: 8, border: "1px solid var(--border-card)",
    background: "var(--bg-card)", color: disabled ? "var(--text-secondary)" : "var(--text-primary)",
    cursor: disabled ? "default" : "pointer", fontSize: 13, opacity: disabled ? 0.4 : 1,
  });
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 10, marginTop: 16 }}>
      <button style={btnStyle(page === 1)} disabled={page === 1} onClick={() => onChange(page - 1)}>
        <i className="fa-solid fa-chevron-left" />
      </button>
      <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>{page} / {pages}</span>
      <button style={btnStyle(page === pages)} disabled={page === pages} onClick={() => onChange(page + 1)}>
        <i className="fa-solid fa-chevron-right" />
      </button>
    </div>
  );
}

export default function AdminAvisos() {
  const { t } = useTranslation();
  const { showToast, toastEl } = useToast();

  const [avisos, setAvisos]     = useState([]);
  const [filiales, setFiliales] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState({ titulo: "", mensaje: "", destinatarios: "TODOS", filiales_ids: [] });
  const [file, setFile]         = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [confirm,  setConfirm]  = useState(null);
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef();

  // Filters
  const [search,          setSearch]          = useState("");
  const [filterFilial,    setFilterFilial]    = useState("");
  const [filterFechaDesde, setFilterFechaDesde] = useState("");
  const [filterFechaHasta, setFilterFechaHasta] = useState("");
  const [page, setPage] = useState(1);

  function load() {
    getAvisos().then(d => setAvisos(d.results || d)).catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => {
    load();
    apiFetch("/api/residents/filial/")
      .then(r => r.json())
      .then(d => setFiliales(d.results || d))
      .catch(() => {});
  }, []);

  function resetPage() { setPage(1); }

  const filtered = avisos.filter(a => {
    if (search && !a.titulo.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterFilial) {
      const fid = parseInt(filterFilial, 10);
      if (a.destinatarios !== "TODOS" && !(a.filiales_ids || []).includes(fid)) return false;
    }
    const fecha = a.created_at?.split("T")[0] || "";
    if (filterFechaDesde && fecha < filterFechaDesde) return false;
    if (filterFechaHasta && fecha > filterFechaHasta) return false;
    return true;
  });
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("titulo", form.titulo);
      fd.append("mensaje", form.mensaje);
      fd.append("destinatarios", form.destinatarios);
      form.filiales_ids.forEach(id => fd.append("filiales_ids", id));
      if (file) fd.append("archivo", file);
      await crearAviso(fd);
      showToast(t("portal.avisoEnviado"), true);
      setForm({ titulo: "", mensaje: "", destinatarios: "TODOS", filiales_ids: [] });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
      setTimeout(() => setModal(false), 1500);
    } catch (e) {
      try { const d = JSON.parse(e.message); showToast(Object.values(d).flat().join(". "), false); }
      catch { showToast(e.message, false); }
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!confirm) return;
    setDeleting(true);
    try { await deleteAviso(confirm.id); showToast(t("common.eliminado"), true); setConfirm(null); load(); }
    catch { showToast(t("common.errorEliminar"), false); }
    finally { setDeleting(false); }
  }

  function toggleFilial(id) {
    setForm(f => ({
      ...f,
      filiales_ids: f.filiales_ids.includes(id)
        ? f.filiales_ids.filter(x => x !== id)
        : [...f.filiales_ids, id],
    }));
  }

  const inputStyle = {
    padding: "7px 10px", borderRadius: 8, border: "1px solid var(--border-input)",
    background: "var(--hover-bg)", color: "var(--text-primary)", fontSize: 13,
    outline: "none", minWidth: 0,
  };

  return (
    <div style={{ padding: "24px" }}>
      {toastEl}
      <ConfirmModal open={!!confirm} name={confirm?.name} onConfirm={handleDelete} onCancel={() => setConfirm(null)} loading={deleting} />

      {/* Breadcrumb */}
      <span style={{ color: "var(--text-secondary)", fontWeight: 400 }}>
        <Link to="/" style={{ textDecoration: "none" }}>
          <span style={{ color: "var(--text-secondary)", fontWeight: 400 }}>Portal Admin /</span>
        </Link>
        <span style={{ color: "var(--text-primary)", fontWeight: 600 }}> {t("portal.avisosAdmin")}</span>
      </span>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2" style={{ marginBottom: 16 }}>
        <div>
          <h2 className="mt-2 me-2 p-0 title">{t("portal.avisosAdmin")}</h2>
          <p className="mb-0" style={{ color: "var(--text-muted)" }}>{t("portal.avisosAdminDesc")}</p>
        </div>
        <button onClick={() => setModal(true)} style={{
          background: "#f97316", color: "#fff", border: "none", borderRadius: 8,
          padding: "9px 16px", cursor: "pointer", fontWeight: 700, fontSize: 13,
          display: "flex", alignItems: "center", gap: 6, marginTop: 8,
        }}>
          <i className="fa-solid fa-paper-plane" /> {t("portal.enviar")}
        </button>
      </div>

      {/* Filter bar */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <input value={search} onChange={e => { setSearch(e.target.value); resetPage(); }}
          placeholder={t("portal.buscarTitulo") || "Buscar título…"}
          style={{ ...inputStyle, flex: "1 1 180px" }} />
        <select value={filterFilial} onChange={e => { setFilterFilial(e.target.value); resetPage(); }}
          style={{ ...inputStyle, flex: "1 1 140px" }}>
          <option value="">{t("portal.todasLasFiliales")}</option>
          {filiales.map(f => <option key={f.id} value={f.id}>{f.idFilial}</option>)}
        </select>
        <input type="date" value={filterFechaDesde} onChange={e => { setFilterFechaDesde(e.target.value); resetPage(); }}
          title="Desde" style={{ ...inputStyle, flex: "1 1 130px" }} />
        <input type="date" value={filterFechaHasta} onChange={e => { setFilterFechaHasta(e.target.value); resetPage(); }}
          title="Hasta" style={{ ...inputStyle, flex: "1 1 130px" }} />
        {(search || filterFilial || filterFechaDesde || filterFechaHasta) && (
          <button onClick={() => { setSearch(""); setFilterFilial(""); setFilterFechaDesde(""); setFilterFechaHasta(""); setPage(1); }} style={{
            padding: "7px 12px", borderRadius: 8, border: "none",
            background: "rgba(249,115,22,0.1)", color: "#f97316",
            cursor: "pointer", fontSize: 12, fontWeight: 700,
          }}>
            <i className="fa-solid fa-xmark" style={{ marginRight: 4 }} />{t("common.limpiar")}
          </button>
        )}
      </div>

      {/* List */}
      {loading ? (
        <p style={{ textAlign: "center", color: "var(--text-secondary)" }}>{t("common.cargando")}</p>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", color: "var(--text-secondary)", padding: 48 }}>
          <i className="fa-solid fa-bell" style={{ fontSize: 40, opacity: 0.4, marginBottom: 12, display: "block" }} />
          <p style={{ margin: 0 }}>{avisos.length === 0 ? t("portal.sinAvisosEnviados") : t("common.sinResultados") || "Sin resultados"}</p>
        </div>
      ) : (
        <>
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 12, overflow: "hidden" }}>
            {paged.map((a, i) => (
              <div key={a.id} style={{
                display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                padding: "14px 16px",
                borderBottom: i < paged.length - 1 ? "1px solid var(--border-card)" : "none",
              }}>
                <div style={{ flex: 1, marginRight: 12 }}>
                  <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: "var(--text-primary)" }}>{a.titulo}</p>
                  <p style={{ margin: "2px 0 4px", fontSize: 12, color: "var(--text-secondary)" }}>
                    {a.created_at?.split("T")[0]} ·{" "}
                    <span style={{
                      background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.3)",
                      color: "#f97316", borderRadius: 20, padding: "1px 7px", fontSize: 11, fontWeight: 700,
                    }}>
                      {a.destinatarios}
                    </span>
                  </p>
                  <p style={{
                    margin: 0, fontSize: 13, color: "var(--text-secondary)",
                    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                  }}>
                    {a.mensaje}
                  </p>
                </div>
                <button onClick={() => setConfirm({ id: a.id, name: a.titulo })} style={{
                  background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)",
                  borderRadius: 8, padding: "6px 10px", cursor: "pointer", color: "#f87171",
                  fontSize: 12, flexShrink: 0,
                }}>
                  <i className="fa-solid fa-trash" />
                </button>
              </div>
            ))}
          </div>
          <Pagination page={page} total={filtered.length} onChange={setPage} />
        </>
      )}

      {/* Create modal */}
      {modal && (
        <div onClick={() => setModal(false)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(3px)",
          zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "var(--bg-card)", border: "1px solid var(--border-card)",
            borderRadius: 12, padding: 24, width: "min(560px,94vw)",
            boxShadow: "0 24px 64px var(--shadow)", maxHeight: "90vh", overflowY: "auto",
          }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: "var(--text-primary)", margin: "0 0 20px" }}>
              {t("portal.nuevoAviso")}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", display: "block", marginBottom: 6, letterSpacing: 0.5 }}>
                  {t("portal.titulo")} <span style={{ color: "#f87171" }}>*</span>
                </label>
                <input value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} required
                  className="form-control" />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", display: "block", marginBottom: 6, letterSpacing: 0.5 }}>
                  {t("portal.mensaje")} <span style={{ color: "#f87171" }}>*</span>
                </label>
                <textarea value={form.mensaje} onChange={e => setForm(f => ({ ...f, mensaje: e.target.value }))} required rows={5}
                  className="form-control" style={{ resize: "vertical", width: "100%", boxSizing: "border-box" }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", display: "block", marginBottom: 6, letterSpacing: 0.5 }}>
                  {t("portal.destinatarios")}
                </label>
                <select value={form.destinatarios} onChange={e => setForm(f => ({ ...f, destinatarios: e.target.value, filiales_ids: [] }))}
                  className="form-control">
                  <option value="TODOS">{t("portal.todasLasFiliales")}</option>
                  <option value="SELECCIONADOS">{t("portal.filialesSeleccionadas")}</option>
                </select>
              </div>
              {form.destinatarios === "SELECCIONADOS" && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", display: "block", marginBottom: 6, letterSpacing: 0.5 }}>
                    {t("portal.seleccionarFiliales")}
                  </label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {filiales.map(f => (
                      <button key={f.id} type="button" onClick={() => toggleFilial(f.id)} style={{
                        padding: "6px 12px", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 600,
                        border: "none",
                        background: form.filiales_ids.includes(f.id) ? "#f97316" : "var(--hover-bg)",
                        color: form.filiales_ids.includes(f.id) ? "#fff" : "var(--text-secondary)",
                      }}>
                        {f.idFilial}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", display: "block", marginBottom: 6, letterSpacing: 0.5 }}>
                  {t("portal.archivoOpcional")}
                </label>
                <div onClick={() => fileRef.current?.click()} style={{
                  border: `1px dashed ${file ? "#f97316" : "var(--border-input)"}`,
                  borderRadius: 10, padding: "14px", textAlign: "center", cursor: "pointer",
                  background: file ? "rgba(249,115,22,0.06)" : "var(--hover-bg)",
                }}>
                  <i className={`fa-solid ${file ? "fa-file-check" : "fa-paperclip"}`}
                    style={{ color: file ? "#f97316" : "var(--text-secondary)", marginRight: 6, fontSize: 14 }} />
                  <span style={{ fontSize: 13, color: file ? "#f97316" : "var(--text-secondary)" }}>
                    {file ? file.name : t("portal.adjuntarArchivo")}
                  </span>
                </div>
                <input ref={fileRef} type="file" style={{ display: "none" }} onChange={e => setFile(e.target.files[0] || null)} />
              </div>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button type="button" onClick={() => setModal(false)} style={{
                  padding: "9px 18px", borderRadius: 8, border: "1px solid var(--border-input)",
                  background: "var(--hover-bg)", color: "var(--text-secondary)", cursor: "pointer", fontSize: 14,
                }}>
                  {t("common.cancelar")}
                </button>
                <button type="submit" disabled={saving} style={{
                  padding: "9px 20px", borderRadius: 8, border: "none",
                  background: "#f97316", color: "#fff", fontWeight: 700, fontSize: 14,
                  cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1,
                }}>
                  {saving ? t("common.cargando") : t("portal.enviarAviso")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
